import { default as languageStrings } from '../../global/l10n/OpnSnapshot.json';
import Util from "../../global/util";
import GPNDao from "../gpn-snapshot/gpn-snapshot.dao";
import { GPNParameters, GpnStockForecast, Inventory, LikePart, OPNMatch, OPNParameters, OpnStockForecast, Price } from "./opn-snapshot.interface";

export class OPNDaoConfig {
    tiHost: string = '';
    productModelApiUrl: string = '/productmodel/{gpn}/orderables';
    searchApiUrl: string = '/search/opn';
    customReelFeesApiUrl: string = '/search/customReelFees';
}

export default class OPNDao {

    public static async searchOPNMatch(searchTerm: string, locale: string, config?: OPNDaoConfig): Promise<OPNMatch> {
        config = config || new OPNDaoConfig();
        const match: OPNMatch = await OPNDao.fetchOPNMatch(searchTerm, locale, config);
        return match;
    }

    public static config(tiHost?: string, productModelApiUrl?: string, searchApiUrl?: string, customReelFeesApiUrl?: string): OPNDaoConfig {
        const config: OPNDaoConfig = new OPNDaoConfig();
        config.customReelFeesApiUrl = customReelFeesApiUrl || config.customReelFeesApiUrl;
        config.productModelApiUrl = productModelApiUrl || config.productModelApiUrl;
        config.searchApiUrl = searchApiUrl || config.searchApiUrl;
        config.tiHost = tiHost || config.tiHost;
        return config;
    }

    public static async fetchOPNMatch(searchTerm: string, locale: string, config?: OPNDaoConfig): Promise<OPNMatch> {
        searchTerm = searchTerm.trim().toUpperCase();
        config = config || new OPNDaoConfig();
        const url = `${config.tiHost}${config.searchApiUrl}?searchTerm=${searchTerm}&locale=${locale}`;
        const response = await fetch(url);
        if (!response.ok) {
            return null;
        }
        const json = await response.json();
        if (!json || !json.orderablePartNumber) {
            return null;
        }

        const match: OPNMatch = {} as OPNMatch;
        match.opn = json.orderablePartNumber;
        match.gpn = {} as GPNParameters;
        match.gpn.genericPartNumber = json.genericPartNumber;

        // gpn parameters
        match.gpn.chipshotUrl = `${config.tiHost}/graphics/folders/partimages/${match.gpn.genericPartNumber}.jpg`;
        match.gpn.datasheetUrl = match.gpn.genericPartNumber && json.gpnDatasheetLitNum ? `${config.tiHost}${languageStrings[locale]["dataSheetUrl"]}${match.gpn.genericPartNumber.toLowerCase()}` : null;
        match.gpn.htmlDocUrl = match.gpn.genericPartNumber && json.gpnDatasheetLitNum &&json.hasHtmlDoc ? `${config.tiHost}${languageStrings[locale]["docSheetUrl"]}${match.gpn.genericPartNumber.toLowerCase()}/datasheet` : null;
        match.gpn.deviceDescription = json.deviceDescription;
        match.gpn.marketingStatusId = json.gpnMarketingStatusId;
        match.gpn.statusDescription = json.gpnStatusDescription;
        match.gpn.statusLongDesc = json.gpnStatusLongDesc;
        match.gpn.newFlag = json.gpnNewFlag;
        match.gpn.gpnFamilyId = json.gpnFamilyId;
        match.gpn.gpnFamilyName = json.gpnFamilyName;
        match.gpn.gpnFamilyUrl = json.gpnFamilyUrl ? json.gpnFamilyUrl.replace('overview.html', 'products.html') : '';
        match.gpn.hasAlternates = json.gpnHasAlternates;
        // custom reel fees
        const customReelFees: Price[] = json.customReelFees as Array<Price>
        if (customReelFees) {
            match.customReelPriceList = JSON.stringify(customReelFees.map(currencyData => {
                return {
                    "currency": currencyData.currency,
                    "currency_price": currencyData.currencyPrice ? currencyData.currencyPrice.toString() : ''
                }
            }));
        }
        // gpn inventory
        const gpnInventory: Map<String, Inventory> = await GPNDao.fetchGPNInventory(match.gpn.genericPartNumber);
        const gpnInventoryForecast: GpnStockForecast = await GPNDao.fetchGPNInventoryForecast(match.gpn.genericPartNumber, locale);
        // opn parameters
        let opnParts = json.likeParts as Array<LikePart>; // "like" parts
        // remove APNs that are not orderable
        opnParts = opnParts.filter(part => !(part.isAPN && !part.isOrderable));
        const opnParameterPromises = [] as Array<Promise<OPNParameters>>;
        opnParts.map(opn => {
            opnParameterPromises.push(OPNDao.fetchOPNParameters(opn.orderablePartNumber, match.gpn.genericPartNumber, locale, config));
        })
        match.opnLike = [] as Array<OPNParameters>;
        await Promise.all(opnParameterPromises).then(opnParameters => {
            opnParameters.map(opn => {
                if (opn) {
                    // opn inventory
                    OPNDao.setOPNInventory(gpnInventory, opn);
                    // opn forecast
                    const opnInventoryForecast = OPNDao.getOPNInventoryForecast(gpnInventoryForecast, opn.orderablePartNumber);
                    opn.inventoryForecast = opnInventoryForecast && opnInventoryForecast.forecast ? opnInventoryForecast.forecast : null;
                    if (opn.orderablePartNumber.toLocaleLowerCase() === match.opn.toLocaleLowerCase()) {
                        match.opnMatch = opn;   // self
                    }
                    else {
                        if (opn.availableForPurchaseFlag === 'Y') {    // display like parts only if it is available for purchase
                            match.opnLike.push(opn);    // "like" parts
                        }
                    }
                }
            })
        });
        match.currencyExchangeRateMap = await GPNDao.fetchCurrencyExchangeRate(match.gpn.genericPartNumber, locale);
        return match;
    }

    public static async fetchOPNParameters(opn: string, gpn: string, locale: string, config: OPNDaoConfig): Promise<OPNParameters> {
        const url = Util.formatUrl(`${config.tiHost}${config.productModelApiUrl}?locale=${locale}&orderable=${opn}`, { gpn: gpn });
        const response = await fetch(url);
        if (!response.ok) {
            return null;
        }
        const json = await response.json();
        const opnParameters: OPNParameters = json as OPNParameters;
        return opnParameters;
    }

    public static setOPNInventory(gpnInventory: Map<String, Inventory>, opn: OPNParameters): void {
        if (gpnInventory === null) {
            opn.loggedOut = true;
            return;
        }
        const opnEntry = gpnInventory[opn.orderablePartNumber];
        if (opnEntry) {
            opn.inventory = opnEntry.inventory.toString();
            opn.showFutureInventory = opnEntry.showFutureInventory;
            opn.orderLimit = opnEntry.orderLimit;
        } else {
            opn.inventory = '0';
            opn.showFutureInventory = false;
            opn.orderLimit = 999999;
        }
    }

    public static async fetchCustomReelFees(config: OPNDaoConfig): Promise<Array<Price>> {
        const url = `${config.tiHost}${config.customReelFeesApiUrl}`;
        const response = await fetch(url);
        if (!response.ok) {
            return [];
        }
        const json = await response.json();
        const fees: Array<Price> = json as Array<Price>;
        return fees;
    }

    public static getOPNInventoryForecast(gpnForecast: GpnStockForecast, opn: string): OpnStockForecast {
        const opnEntry: OpnStockForecast = gpnForecast[opn];
        return opnEntry ? opnEntry : null;
    }

}
